import {
  type AllianzRating,
  type CreditClassification,
  type FitchRating,
  type JcrRating,
  type MoodysRating,
  type SpRating
} from '@klear/constants'
import { Money, type MoneyJson } from '@klear/money'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'

import type { CreditCreateWire, CreditUpdateWire } from '@klear/credit/credit.wire'

type MoneyFormValue = Money | MoneyJson

export type CreditForm = {
  moodysRating?: MoodysRating
  spRating?: SpRating
  fitchRating?: FitchRating
  jcrRating?: JcrRating
  allianzRating?: AllianzRating
  allianzCreditLimit?: MoneyFormValue | null
  creditClassification?: CreditClassification
}

export type CreditFormDirty = Partial<Record<keyof CreditForm, boolean | object>>

type CreditFormSource = {
  moodysRating?: MoodysRating
  spRating?: SpRating
  fitchRating?: FitchRating
  jcrRating?: JcrRating
  allianzRating?: AllianzRating
  allianzCreditLimit?: MoneyFormValue | { toJSON(): MoneyFormValue } | null
  creditClassification?: CreditClassification
}

export const EMPTY_CREDIT_FORM: CreditForm = {}

function optionalMoney(value: MoneyFormValue | null | undefined): MoneyJson | undefined {
  return value == null ? undefined : new Money(value).toJSON()
}

function clearableMoney(value: MoneyFormValue | null | undefined): MoneyJson | null {
  return optionalMoney(value) ?? null
}

function hasToJSON<T>(value: unknown): value is { toJSON(): T } {
  return value != null && isFunction((value as { toJSON?: unknown }).toJSON)
}

function sourceMoney(source: CreditFormSource['allianzCreditLimit']): MoneyFormValue | undefined {
  if (source == null) return undefined
  return hasToJSON<MoneyFormValue>(source) ? source.toJSON() : source
}

function isDirty(dirty: CreditFormDirty, key: keyof CreditForm): boolean {
  return dirty[key] === true || (dirty[key] != null && isObject(dirty[key]))
}

export function creditReadToForm(source: CreditFormSource): CreditForm {
  return {
    ...EMPTY_CREDIT_FORM,
    moodysRating: source.moodysRating,
    spRating: source.spRating,
    fitchRating: source.fitchRating,
    jcrRating: source.jcrRating,
    allianzRating: source.allianzRating,
    creditClassification: source.creditClassification,
    allianzCreditLimit: sourceMoney(source.allianzCreditLimit)
  }
}

export function creditFormToCreateWire(form: CreditForm): CreditCreateWire {
  const wire: CreditCreateWire = {}

  if (form.moodysRating != null) wire.moodysRating = form.moodysRating
  if (form.spRating != null) wire.spRating = form.spRating
  if (form.fitchRating != null) wire.fitchRating = form.fitchRating
  if (form.jcrRating != null) wire.jcrRating = form.jcrRating
  if (form.allianzRating != null) wire.allianzRating = form.allianzRating
  const allianzCreditLimit = optionalMoney(form.allianzCreditLimit)
  if (allianzCreditLimit !== undefined) wire.allianzCreditLimit = allianzCreditLimit
  if (form.creditClassification != null) wire.creditClassification = form.creditClassification

  return wire
}

export function creditFormToUpdateWire(form: CreditForm, dirty: CreditFormDirty): CreditUpdateWire {
  const wire: CreditUpdateWire = {}

  if (isDirty(dirty, 'moodysRating')) wire.moodysRating = form.moodysRating ?? null
  if (isDirty(dirty, 'spRating')) wire.spRating = form.spRating ?? null
  if (isDirty(dirty, 'fitchRating')) wire.fitchRating = form.fitchRating ?? null
  if (isDirty(dirty, 'jcrRating')) wire.jcrRating = form.jcrRating ?? null
  if (isDirty(dirty, 'allianzRating')) wire.allianzRating = form.allianzRating ?? null
  if (isDirty(dirty, 'allianzCreditLimit')) wire.allianzCreditLimit = clearableMoney(form.allianzCreditLimit)
  if (isDirty(dirty, 'creditClassification')) wire.creditClassification = form.creditClassification ?? null

  return wire
}

export function creditFormDirtyIsEmpty(dirty: CreditFormDirty): boolean {
  return !(
    isDirty(dirty, 'moodysRating') ||
    isDirty(dirty, 'spRating') ||
    isDirty(dirty, 'fitchRating') ||
    isDirty(dirty, 'jcrRating') ||
    isDirty(dirty, 'allianzRating') ||
    isDirty(dirty, 'allianzCreditLimit') ||
    isDirty(dirty, 'creditClassification')
  )
}
